/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {useNavigate, useParams } from "react-router-dom";
import { getProductDetail } from "../../services/getProductDetail";
import {
  Box,
  Button,
  Fade,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { IPrice, IProductDetail } from "../../interfaces/shopify";
import { colorDoubleMap } from "../../helpers/Colors";
import { BoxCheck, CartPlus, Check, GridOrder, Plus, Share } from "../../icons";
import { blackTextColors } from "../../utils/CheckColor";
import { ChipState } from "../../components/ChipState";
import { EmblaDetail } from "../../components/EmblaDetail/EmblaDetail";
import { LoadPage } from "../../components/LoadPage";
import {
  buildLayeredBackground,
  cleanColorName,
  isUrl,
  onShare,
  sortSizes,
  toColor,
} from "../../utils/funtions";
import { InputQuantity } from "../../components/InputQuantity/InputQuantity";
import { Line } from "../../components/Line";
import { CarruselFeatured } from "../../components/CarruselFeatured";
import { DialogPickup } from "../../components/ModalDetail";
import { useLenis } from "../../hooks/useLenis";
import { ImageDetail } from "../../components/ImageDetail/ImageDetail";
import { useCheckoutStore } from "../../store/checkoutStore";
import { ChipAvailable } from "../../components/ChipAvailable";
import { DialogTableOrder } from "../../components/DialogTableOrder/DialogTableOrder";

export const ProductDetail = () => {
  const { id } = useParams();
  const [detail, setDetail] = useState<IProductDetail | null>();
  const [pickup, setPickup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [variantPrice, setVariantPrice] = useState<IPrice>();
    const [variantComparePrice, setVariantComparePrice] = useState<IPrice>();
  const [variantId, setVariantId] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [inStock, setInStock] = useState<{ availableForSale: boolean }>();
  const [tableOrder, setTableOrder] = useState(false);

  const [available, setAvailable] = useState<{ quantityAvailable: number }>({
    quantityAvailable: 0,
  });

  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});
  const theme = useTheme();

  // 🔒 Memoiza para no crear nuevas referencias en cada render
  const allVariants = useMemo(
    () =>
      detail?.productVariantMetafield?.references?.edges.flatMap(
        (r) => r.node.variants.edges ?? []
      ) ?? [],
    [detail]
  );

  const allOptionNames = useMemo(
    () =>
      Array.from(
        new Set(
          allVariants.flatMap((v) =>
            v.node.selectedOptions.map((opt) => opt.name)
          )
        )
      ),
    [allVariants]
  );

  const getOptionsFor = (name: string) =>
    Array.from(
      new Set(
        allVariants.flatMap((v) =>
          v.node.selectedOptions
            .filter((opt) => opt.name === name)
            .map((opt) => opt.value)
        )
      )
    );

  useEffect(() => {
    setLoading(true);
    const getData = async () => {
      try {
        if (!id) return;
        const data = await getProductDetail(id);
        setDetail(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [id]);

  // Inicializa con variante válida (preferible disponible)
  useEffect(() => {
    if (!detail) return;
    if (!allVariants.length) return;

    const first =
      allVariants.find((v) => v.node.availableForSale) ?? allVariants[0];

    const initial: Record<string, string> = {};
    first.node.selectedOptions.forEach((o: any) => {
      initial[o.name] = o.value;
    });

    setSelectedOptions(initial);
    setVariantId(first.node.id);

    if (first.node.price) {
      setVariantPrice({
        amount: first.node.price.amount,
        currencyCode: first.node.price.currencyCode,
      });
 

    } else {
      setVariantPrice(undefined);
    }

    setInStock({ availableForSale: !!first.node.availableForSale });
    setAvailable({ quantityAvailable: first.node.quantityAvailable ?? 0 });
    // deps: detail y allVariants son estables (memo)
  }, [detail, allVariants]);

  // Helpers
  const matchesHypothetical = (
    variant: any,
    hyp: Record<string, string>
  ): boolean =>
    Object.entries(hyp).every(([n, val]) =>
      variant.node.selectedOptions.some(
        (o: any) => o.name === n && o.value === val
      )
    );

  const shallowEqual = (
    a: Record<string, string>,
    b: Record<string, string>
  ) => {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    for (const k of ka) if (a[k] !== b[k]) return false;
    return true;
  };

  // “Snap” a combinación válida + evita setState innecesario
  const selectOption = (name: string, value: string) => {
    const hyp = { ...selectedOptions, [name]: value };

    let match = allVariants.find((v) => matchesHypothetical(v, hyp));

    if (!match) {
      match = allVariants.find((v) =>
        v.node.selectedOptions.some(
          (o: any) => o.name === name && o.value === value
        )
      );
      if (match) {
        match.node.selectedOptions.forEach((o: any) => {
          hyp[o.name] = o.value;
        });
      }
    }

    if (!shallowEqual(selectedOptions, hyp)) {
      setSelectedOptions(hyp);
    }
  };

  const isValueDisabled = (name: string, value: string) => {
    const hyp = { ...selectedOptions, [name]: value };
    return !allVariants.some((v) => matchesHypothetical(v, hyp));
  };

  // Sincroniza estado derivado de la selección (deps estables)
  useEffect(() => {
    if (!allVariants.length) return;
    if (!Object.keys(selectedOptions).length) return;

    const match = allVariants.find((v) =>
      v.node.selectedOptions.every(
        (opt: any) => selectedOptions[opt.name] === opt.value
      )
    );

    const newVariantId = match?.node.id ?? null;
    if (newVariantId !== variantId) setVariantId(newVariantId);

    const newPrice = match?.node.price
      ? {
          amount: match.node.price.amount,
          currencyCode: match.node.price.currencyCode,
        }
      : undefined;

    // evita sets repetidos
    if (
      (newPrice?.amount ?? "") !== (variantPrice?.amount ?? "") ||
      (newPrice?.currencyCode ?? "") !== (variantPrice?.currencyCode ?? "")
    ) {
      setVariantPrice(newPrice);
    }

      const newCompareAtPrice = match?.node.compareAtPrice
      
       if (
      (newCompareAtPrice?.amount ?? "") !== (variantPrice?.amount ?? "") ||
      (newCompareAtPrice?.currencyCode ?? "") !== (variantPrice?.currencyCode ?? "")
    ) {
      setVariantComparePrice(newCompareAtPrice);
    }

    const newInStock = !!match?.node?.availableForSale;
    if (newInStock !== !!inStock?.availableForSale) {
      setInStock({ availableForSale: newInStock });
    }

    const newQty = match?.node?.quantityAvailable ?? 0;
    if (newQty !== (available?.quantityAvailable ?? 0)) {
      setAvailable({ quantityAvailable: newQty });
    }
  }, [allVariants, selectedOptions]); // <- referencias memo + objeto estable

  const gridContentRef = useRef<HTMLDivElement>(null);

  const { addVariant } = useCheckoutStore();
  const navigate = useNavigate();

  const onAddToCart = async (variantId: string | null, isBuyNow: boolean) => {
    if (
      !variantId ||
      !variantPrice ||
      !inStock?.availableForSale ||
      (available?.quantityAvailable ?? 0) <= 0
    )
      return;

    let price = parseFloat(variantPrice.amount).toFixed(2);
    addVariant({
      variants: variantId,
      available: available.quantityAvailable,
      amount: price,
      qty: qty,
    });
    if (isBuyNow) navigate("/checkout");
  };

  useLenis(0.8, tableOrder);

  return (
    <React.Fragment>
      {loading ? (
        <LoadPage />
      ) : (
        <Fade in timeout={1000}>
          <Box>
            <Grid
              display="flex"
              justifyContent="center"
              container
              spacing={10}
              marginTop={{ xs: 5, md: 12 }}
              marginInline={3}
            >
              <Grid maxWidth={450} size={{ xs: 12, md: 6, lg: 5 }}>
                <EmblaDetail
                  ref={gridContentRef}
                  slides={detail ? detail.images.edges : []}
                  options={{
                    loop: true,
                    duration: 25,
                  }}
                />
              </Grid>

              <Grid
                maxWidth={600}
                size={{ xs: 12, md: 5, lg: 4 }}
                display="flex"
                alignItems="flex-start"
                flexDirection="column"
                gap={5}
              >
                <Grid display="flex" flexDirection="column" gap={3} size={12}>
                  <Box
                    height={40}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <ChipState state={inStock?.availableForSale} />
                    <IconButton
                      onClick={onShare}
                      sx={{ borderRadius: "100vmax !important" }}
                    >
                      <Share htmlColor={theme.palette.secondary[100]} />
                    </IconButton>
                  </Box>
                  <Typography variant="h5">{detail?.title}</Typography>
                  <Box display="flex" alignItems="center" gap={5}>  
                     <Typography variant="h6" color="warning" sx={{display: variantComparePrice?.amount ? "flex" : "none", textDecoration: "line-through"}}>
                    ${variantComparePrice?.amount ?? "--"}
                  </Typography>
                     <Typography variant="h6">
                    ${variantPrice?.amount ?? "--"}
                  </Typography>
               
                  </Box>
                 
                  <Box
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",

                      "& *": {
                        display: "inline",
                        margin: 0,
                        padding: 0,
                        whiteSpace: "normal",
                        verticalAlign: "baseline",
                      },
                      "& br": { display: "none" },

                      ".product_details, .specifications, .title": {
                        display: "none !important",
                      },

                      ".paragraph": { display: "inline !important" },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: detail?.descriptionHtml ?? "",
                    }}
                  />
                </Grid>
                <Line />
                <Grid
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  size={12}
                >
                  <ChipAvailable available={available?.quantityAvailable} />

                  <Button
                    size="small"
                    onClick={() => setPickup(true)}
                    startIcon={
                      <BoxCheck
                        htmlColor={theme.palette.secondary[100]}
                        sx={{ width: 14, height: 14 }}
                      />
                    }
                    sx={{
                      fontWeight: "600 !important",
                      backgroundColor: theme.palette.customColor?.main,
                      padding: "5px 12px",
                      color: theme.palette.secondary[100],
                    }}
                  >
                    Store Pickup
                  </Button>
                </Grid>

                {[
                  "Color",
                  "Size",
                  ...allOptionNames.filter(
                    (o) => o !== "Color" && o !== "Size"
                  ),
                ].map((optionName) => {
                  let values = getOptionsFor(optionName);
                  if (optionName === "Size") {
                    values = sortSizes(values);
                  }
                  if (values.length === 0) return null;

                  return (
                    <Box
                      key={optionName}
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      width="100%"
                    >
                      <Typography
                        variant="body2"
                        textTransform="uppercase"
                        fontWeight={700}
                        sx={{
                          color: theme.palette.secondary[500],
                          display: optionName === "Title" ? "none" : "flex",
                        }}
                      >
                        {optionName}
                      </Typography>

                      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                        {optionName === "Color"
                          ? values.map((c) => {
                              const name = cleanColorName(c).replace(
                                /[\s/]+/g,
                                ""
                              );
                              const isSlash = c.includes("/");

                              let stops: string[] = [];
                              if (isSlash) {
                                const arr = colorDoubleMap[name] ?? [
                                  "blue",
                                  "red",
                                ];

                                stops = Array.isArray(arr)
                                  ? arr.slice(0, 3)
                                  : ["blue", "red"];
                              } else {
                                if (isUrl(name)) stops = [name];
                                else stops = [toColor(name)];
                              }
                              const bg = buildLayeredBackground(stops);

                              const selected =
                                selectedOptions[optionName] === c;
                              const disabled = isValueDisabled(optionName, c);

                              return (
                                <Tooltip
                                  key={c}
                                  title={c}
                                  arrow
                                  placement="top"
                                >
                                  <span>
                                    <Button
                                      disabled={disabled}
                                      sx={{
                                        boxShadow:
                                          "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
                                        minWidth: 35,
                                        minHeight: 35,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        borderRadius: "8px !important",
                                        objectFit: "cover",
                                        backgroundPosition: "center",
                                        backgroundSize: "cover",
                                        backgroundRepeat: "no-repeat",
                                        ...(bg.backgroundImage
                                          ? {
                                              backgroundImage: (bg as any)
                                                .backgroundImage,
                                            }
                                          : {}),
                                        ...(bg.backgroundColor
                                          ? {
                                              backgroundColor: (bg as any)
                                                .backgroundColor,
                                            }
                                          : {}),
                                        opacity: disabled ? 0.4 : 1,
                                      }}
                                      onClick={() =>
                                        selectOption(optionName, c)
                                      }
                                    >
                                      <Check
                                        htmlColor={
                                          name === "Black"
                                            ? "#E6E6E6"
                                            : blackTextColors.includes(name)
                                            ? "#1A1A1A"
                                            : "#E6E6E6"
                                        }
                                        sx={{
                                          transition:
                                            "opacity 0.3s ease, transform 0.3s ease",
                                          transform: selected
                                            ? "scale(1)"
                                            : "scale(0.5)",
                                          opacity: selected ? 1 : 0,
                                          width: 17,
                                          height: 17,
                                        }}
                                      />
                                    </Button>
                                  </span>
                                </Tooltip>
                              );
                            })
                          : values.map((val) => {
                              const selected =
                                selectedOptions[optionName] === val;
                              const disabled = isValueDisabled(optionName, val);
                              return (
                                <Button
                                  key={val}
                                  color="secondary"
                                  disableElevation
                                  disabled={disabled}
                                  onClick={() => selectOption(optionName, val)}
                                  sx={{
                                    fontSize: "13px !important",
                                    fontWeight: "600 !important",
                                    borderRadius: "5px !important",
                                    color: selected
                                      ? theme.palette.secondary[500]
                                      : theme.palette.secondary[100],
                                    backgroundColor: selected
                                      ? theme.palette.customColor?.main
                                      : "transparent",
                                    display:
                                      val === "Default Title" ? "none" : "flex",
                                    "&:hover": {
                                      boxShadow: `0px 0px 0px 2px ${theme.palette.secondary[100]}`,
                                    },
                                    boxShadow: selected
                                      ? `0px 0px 0px 2px ${theme.palette.customColor?.light}`
                                      : `0px 0px 0px 1px ${theme.palette.customColor?.light}`,
                                    opacity: disabled ? 0.4 : 1,
                                  }}
                                >
                                  {val}
                                </Button>
                              );
                            })}
                      </Box>
                    </Box>
                  );
                })}
                <Box
                  width="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="body2"
                    textTransform="uppercase"
                    sx={{ color: theme.palette.secondary[500] }}
                    fontWeight={700}
                  >
                    Quantity
                  </Typography>

                  <InputQuantity
                    available={available?.quantityAvailable}
                    isAvailable={!inStock?.availableForSale}
                    setQuantity={setQty}
                    quantity={qty}
                  />
                </Box>

                <Box
                  width="100%"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                >
                  <Box width="100%" gap={2} display="flex" alignItems="center">
                    <Grid size={12}>
                      <Button
                        fullWidth
                        onClick={() => onAddToCart(variantId, true)}
                        disabled={!inStock?.availableForSale}
                        startIcon={
                          <Plus
                            sx={{ width: 16, height: 16 }}
                            htmlColor={theme.palette.customColor?.main}
                          />
                        }
                        variant="contained"
                        disableElevation
                        sx={{
                          color: theme.palette.customColor?.main,
                          bgcolor: theme.palette.secondary.main,
                          p: 3,
                        }}
                      >
                        Buy Now
                      </Button>
                    </Grid>
                    <Grid size={12}>
                      <Button
                        fullWidth
                        onClick={() => onAddToCart(variantId, false)}
                        disabled={!inStock?.availableForSale}
                        startIcon={<CartPlus sx={{ width: 20, height: 20 }} />}
                        variant="contained"
                        disableElevation
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.palette.primary[300],
                          },
                          p: 3,
                        }}
                      >
                        Add To Cart
                      </Button>
                    </Grid>
                  </Box>
                  <Button
                    fullWidth
                    onClick={() => setTableOrder(true)}
                    startIcon={
                      <GridOrder
                        htmlColor={theme.palette.secondary[800]}
                        sx={{ width: 15, height: 15 }}
                      />
                    }
                    sx={{
                      backgroundColor: theme.palette.customColor?.main,
                      p: 3,
                      color: theme.palette.secondary[100],
                    }}
                  >
                    Batch Order
                  </Button>
                </Box>

                <Box
                  ref={gridContentRef}
                  dangerouslySetInnerHTML={{
                    __html: detail?.descriptionHtml ?? "",
                  }}
                />
              </Grid>
            </Grid>

            <Grid
              mt={{ xs: 10, md: 25 }}
              spacing={3}
              marginInline={{ xs: 3, md: 10 }}
              container
            >
              <ImageDetail dataTitle={detail} data={detail} />

              <Grid size={12}>
                <CarruselFeatured
                  to={`/collections/${detail?.collections.edges[0].node.handle}`}
                  sortKey="PRICE"
                  customTitle="You may also like"
                  category={detail?.collections.edges[0].node.handle ?? ""}
                />
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}
      <DialogPickup open={pickup} onClose={() => setPickup(false)} />
      <DialogTableOrder
        onClose={() => setTableOrder(false)}
        open={tableOrder}
        data={detail}
      />
    </React.Fragment>
  );
};
